/**
 * Déroulant de barre — le gabarit commun du thème, de la langue et du compte (exiUI,
 * coquille.md §6.4). Trois règles non négociables, portées ici une fois pour toutes :
 *
 *  - fermeture au clic extérieur ET à Échap ;
 *  - restitution du focus au déclencheur à la fermeture ;
 *  - `aria-haspopup="menu"` + `aria-expanded` sur le déclencheur, `role="menu"` sur le panneau.
 *
 * Le déclencheur est rendu par l'appelant (`declencheur({ ouvert })`) : le thème pose une
 * icône + libellé, le compte un carré + nom. Le panneau porte `MENU` du contrat.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { MENU } from '../../ui/theme.js';

export function Deroulant({ declencheur, label, children, className = '' }) {
  const [ouvert, setOuvert] = useState(false);
  const racine = useRef(null);
  const bouton = useRef(null);
  const idMenu = useId();

  const fermer = useCallback((rendreFocus = true) => {
    setOuvert(false);
    if (rendreFocus) bouton.current?.focus();
  }, []);

  useEffect(() => {
    if (!ouvert) return undefined;
    const surClic = (e) => {
      if (!racine.current?.contains(e.target)) fermer(false);
    };
    const surTouche = (e) => {
      if (e.key === 'Escape') fermer(true);
    };
    document.addEventListener('mousedown', surClic);
    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('mousedown', surClic);
      document.removeEventListener('keydown', surTouche);
    };
  }, [ouvert, fermer]);

  return (
    <div ref={racine} className={`relative ${className}`}>
      <button
        ref={bouton}
        type="button"
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-controls={idMenu}
        aria-label={label}
        onClick={() => setOuvert((o) => !o)}
        className="block"
      >
        {declencheur({ ouvert })}
      </button>
      {ouvert && (
        <div
          id={idMenu}
          role="menu"
          aria-label={label}
          className={MENU}
          onClick={() => fermer(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/** Chevron du déclencheur — pivote à l'ouverture. Tracé carré, 2 px, comme tout le reste. */
export function Chevron({ ouvert }) {
  return (
    <svg
      viewBox="0 0 10 6"
      className={`h-2 w-2.5 shrink-0 transition-transform ${ouvert ? 'rotate-180' : ''}`}
      fill="none"
      aria-hidden="true"
    >
      <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="square" />
    </svg>
  );
}
